import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { Form } from "../../components/Form";
import { Input } from "../../components/Input";
import { spacing } from "@styling/sizing";
import type {
  RestBookkeepingAccount,
  RestCreateBookkeepingAccount,
} from "@KassaroAB/shared/types/bookkeepingAccount";
import { FormSection } from "../../components/FormSection";

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

interface BookkeepingAccountFormProps {
  formId: string;
  account?: RestBookkeepingAccount;
  savedTick?: number;
  onSubmit: (data: RestCreateBookkeepingAccount) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const BookkeepingAccountForm = ({
  formId,
  account,
  savedTick,
  onSubmit,
  onDirtyChange,
}: BookkeepingAccountFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    defaultValues: {
      name: account?.name ?? "",
      accountNumber: account?.accountNumber ?? "",
    },
    onSubmit: ({ value }) => {
      onSubmit({
        name: value.name,
        accountNumber: value.accountNumber,
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!savedTick) return;
    form.reset(form.state.values);
  }, [savedTick, form]);

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <FormSection title={t("bookkeepingAccountForm.basicInformation")}>
        <Fields>
          <form.Field name="name">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("bookkeepingAccountForm.name")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="accountNumber">
            {(field) => (
              <FormGroup>
                <FormLabel>
                  {t("bookkeepingAccountForm.accountNumber")}
                </FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </Form>
  );
};
